import React from 'react';

import { ScrollFader, useScrollFaderController } from '@gitbook/spine-react';

import { ContentIcon } from '../Icons';
import { AgentDockTab as AgentDockTabButton } from './AgentDockTab';
import { type AgentDockTab, getAgentDockTabId } from './state';
import { useAgentDockTabStatus } from './useAgentDockTabStatus';

export type AgentDockTabDescriptor = {
    tab: AgentDockTab;
    /** Title to show, already resolved from the conversation. */
    title: string;
};

/**
 * The dock's tab bar.
 *
 * Tabs share the available width, flexing down to the minimum a title stays readable at; past that
 * the row scrolls, so opening many conversations degrades into scrolling rather than into slivers.
 */
export function AgentDockTabs(props: {
    tabs: AgentDockTabDescriptor[];
    activeTabId: string | null;
    organizationId: string;
    /** Hands each tab's element to the dock, which aligns the conversation window with it. */
    registerAnchor?: (tabId: string, element: HTMLElement | null) => void;
    onSelect: (tabId: string) => void;
    onClose: (tabId: string) => void;
}) {
    const { tabs, activeTabId, organizationId, registerAnchor, onSelect, onClose } = props;
    const containerRef = React.useRef<HTMLDivElement>(null);
    const controller = useScrollFaderController('horizontal', containerRef);

    if (tabs.length === 0) {
        return null;
    }

    return (
        <ScrollFader controller={controller} className="min-w-0 flex-1">
            <div
                // Right-aligned so the row grows leftwards from the Agent button rather than
                // drifting away from it across an empty bar.
                className="scrollbar-none flex items-center justify-end gap-0.5 overflow-x-auto"
                ref={containerRef}
            >
                {tabs.map((descriptor) => {
                    const tabId = getAgentDockTabId(descriptor.tab);
                    return (
                        <ConversationTab
                            key={tabId}
                            descriptor={descriptor}
                            active={tabId === activeTabId}
                            organizationId={organizationId}
                            anchorRef={(element) => registerAnchor?.(tabId, element)}
                            onSelect={() => onSelect(tabId)}
                            onClose={() => onClose(tabId)}
                        />
                    );
                })}
            </div>
        </ScrollFader>
    );
}

/**
 * One conversation's tab. A component of its own because watching the conversation is a hook, and
 * the bar renders a list of them.
 */
function ConversationTab(props: {
    descriptor: AgentDockTabDescriptor;
    active: boolean;
    organizationId: string;
    anchorRef: (element: HTMLElement | null) => void;
    onSelect: () => void;
    onClose: () => void;
}) {
    const { descriptor, active, organizationId, anchorRef, onSelect, onClose } = props;
    const status = useAgentDockTabStatus({ organizationId, tab: descriptor.tab });

    return (
        <AgentDockTabButton
            title={descriptor.title}
            active={active}
            {...(status ? { status } : {})}
            {...(descriptor.tab.kind === 'organization_background'
                ? { icon: <BackgroundAgentTabIcon /> }
                : {})}
            anchorRef={anchorRef}
            onSelect={onSelect}
            onClose={onClose}
        />
    );
}

/**
 * The glyph marking a background agent's tab.
 *
 * The bar mixes the user's own conversations with the ones their agent started for itself, and
 * without this they are told apart only by a title someone has to read. Sized on the label's own
 * terms, like the attention marker beside it.
 */
function BackgroundAgentTabIcon() {
    return (
        <ContentIcon
            icon="robot"
            aria-label="Background agent"
            className="size-3.5 shrink-0 text-muted"
        />
    );
}
